/**
 * lazyPrefetch — Prefetch on hover/touch for code-split routes
 *
 * Cómo funciona:
 *   - `routeImports`: mapa central de ruta → import() dinámico.
 *   - `LazyPages`: componentes `React.lazy()` listos para usar en `<Routes>` (App.js).
 *   - `prefetchRoute(path)`: dispara el `import()` correspondiente (fire-and-forget).
 *     Webpack cachea el chunk en la primera llamada → llamadas siguientes son no-op.
 *
 * Uso típico (en items del sidebar/tabs):
 *   <Link to="/reports"
 *         onMouseEnter={() => prefetchRoute('/reports')}
 *         onTouchStart={() => prefetchRoute('/reports')}
 *         onFocus={() => prefetchRoute('/reports')}>
 *
 * Beneficio: cuando el usuario pasa el dedo / cursor por encima de un enlace,
 * el chunk empieza a descargarse antes del clic → UX percibida instantánea
 * sin perder el ahorro inicial de bundle.
 */
import { lazy } from 'react';

// Mapa central: ruta → función de import dinámico
// IMPORTANTE: estas expresiones DEBEN coincidir con las usadas por `lazy()` abajo
// para que webpack reutilice el mismo chunk.
export const routeImports = {
  '/reports': () => import('../pages/ReportsPage'),
  '/calendar': () => import('../pages/CalendarPage'),
  '/tasks': () => import('../pages/TasksPage'),
  '/trends': () => import('../pages/TrendsPage'),
  '/team': () => import('../pages/TeamPage'),
  '/staff-timing': () => import('../pages/StaffTimingPage'),
  '/finances': () => import('../pages/FinancesPage'),
  '/presupuestos': () => import('../pages/PresupuestosPage'),
  '/users': () => import('../pages/UsersPage'),
  '/settings': () => import('../pages/SettingsPage'),
  '/precios': () => import('../pages/PreciosPage'),
  '/mp3-library': () => import('../pages/MP3LibraryPage'),
  '/almacenamiento': () => import('../pages/StoragePage'),
  '/venues': () => import('../pages/VenuesPage'),
  '/wizard-health': () => import('../pages/WizardHealthPage'),
  '/plantillas': () => import('../pages/PlantillasPage'),
  '/dashboard': () => import('../pages/DashboardPage'),
  '/library': () => import('../pages/LibraryPage'),
  '/admin/audit-log': () => import('../pages/AuditLogPage'),
};

/**
 * Prefetch del chunk de una ruta. Idempotente y silencioso ante errores
 * (un fallo de prefetch no debe romper la UI; el `lazy()` lo intentará de nuevo
 * en el clic real y propagará el error correctamente vía Suspense).
 */
export const prefetchRoute = (path) => {
  const imp = routeImports[path];
  if (!imp) return;
  try {
    imp().catch((err) => {
      // Chunk load failed en prefetch: se reintentará al hacer clic real
      console.debug('[lazyPrefetch] prefetch failed (will retry on click):', err?.message);
    });
  } catch (e) {
    console.debug('[lazyPrefetch] prefetch threw:', e?.message);
  }
};

// Componentes lazy listos para `<Routes>` (mismo `import()` que `routeImports`)
export const LazyPages = {
  ReportsPage: lazy(routeImports['/reports']),
  CalendarPage: lazy(routeImports['/calendar']),
  TasksPage: lazy(routeImports['/tasks']),
  TrendsPage: lazy(routeImports['/trends']),
  TeamPage: lazy(routeImports['/team']),
  StaffTimingPage: lazy(routeImports['/staff-timing']),
  FinancesPage: lazy(routeImports['/finances']),
  PresupuestosPage: lazy(routeImports['/presupuestos']),
  UsersPage: lazy(routeImports['/users']),
  SettingsPage: lazy(routeImports['/settings']),
  PreciosPage: lazy(routeImports['/precios']),
  MP3LibraryPage: lazy(routeImports['/mp3-library']),
  StoragePage: lazy(routeImports['/almacenamiento']),
  VenuesPage: lazy(routeImports['/venues']),
  WizardHealthPage: lazy(routeImports['/wizard-health']),
  PlantillasPage: lazy(routeImports['/plantillas']),
  DashboardPage: lazy(routeImports['/dashboard']),
  LibraryPage: lazy(routeImports['/library']),
  AuditLogPage: lazy(routeImports['/admin/audit-log']),
};

/**
 * Helper: props para enganchar prefetch a un elemento navegable.
 * Cubre desktop (mouse) + mobile (touch) + accesibilidad (focus por teclado).
 */
export const prefetchHandlers = (path) => ({
  onMouseEnter: () => prefetchRoute(path),
  onTouchStart: () => prefetchRoute(path),
  onFocus: () => prefetchRoute(path),
});
